/**
 * overlays.tsx — popover menus, the notifications panel, the invoice drawer, bottom sheets,
 * dialogs and toasts, ported from the mockup.
 *
 * Accessibility (spec-business §8.3): sheets, dialogs and the drawer are modal, trap focus,
 * close on Escape and return focus to the opener; menus close on outside pointer-down and
 * Escape. Toasts are `role="status"` (success) / `role="alert"` (error), at most 2 at a time.
 */

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { he } from '../i18n/he.ts';

export interface MenuSpec {
  kind: 'menu';
  anchor: DOMRect;
  content: ReactNode;
  wide?: boolean;
  role?: 'menu' | 'dialog';
  label?: string;
}

export interface PanelSpec {
  kind: 'panel';
  title: string;
  content: ReactNode;
  footer?: ReactNode;
}

export interface SheetSpec {
  kind: 'sheet';
  title: string;
  content: ReactNode;
}

export interface DrawerSpec {
  kind: 'drawer';
  title: ReactNode;
  content: ReactNode;
  footer?: ReactNode;
  label: string;
}

export interface DialogAction {
  label: string;
  tone?: 'primary' | 'danger' | 'ghost';
  /** Pushed to the start of the row (the mockup's `.btn.start`). */
  start?: boolean;
  /** Return false to keep the dialog open. */
  onClick?: () => void | boolean;
  disabled?: boolean;
}

export interface DialogSpec {
  kind: 'dialog';
  title: string;
  text?: string;
  content?: ReactNode;
  actions: DialogAction[];
  wide?: boolean;
  role?: 'dialog' | 'alertdialog';
}

export type LayerSpec = MenuSpec | PanelSpec | SheetSpec | DrawerSpec | DialogSpec;

function useFocusTrap(ref: { current: HTMLElement | null }, onEscape: () => void) {
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, [tabindex="0"]';
    const focusables = () => Array.from(node.querySelectorAll<HTMLElement>(selector)).filter((el) => el.offsetParent !== null);
    const timer = setTimeout(() => focusables()[0]?.focus({ preventScroll: true }), 30);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) return;
      const index = list.indexOf(document.activeElement as HTMLElement);
      if (event.shiftKey && index <= 0) {
        event.preventDefault();
        list[list.length - 1]?.focus({ preventScroll: true });
      } else if (!event.shiftKey && index === list.length - 1) {
        event.preventDefault();
        list[0]?.focus({ preventScroll: true });
      }
    };
    node.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      node.removeEventListener('keydown', onKey);
    };
  }, [ref, onEscape]);
}

export function Menu({ spec, onClose }: { spec: MenuSpec; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const timer = setTimeout(() => document.addEventListener('pointerdown', onPointerDown, true), 0);
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, button, a')?.focus({ preventScroll: true });
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // RTL: anchor the menu to the right edge of its trigger, clamped to the viewport.
  const top = Math.min(spec.anchor.bottom + 6, window.innerHeight - 80);
  const right = Math.max(8, window.innerWidth - spec.anchor.right);
  return (
    <div
      ref={ref}
      className="popover menu"
      role={spec.role ?? 'menu'}
      aria-label={spec.label}
      style={{ top: `${top}px`, right: `${right}px`, left: 'auto', minWidth: spec.wide ? '320px' : undefined }}
    >
      {spec.content}
    </div>
  );
}

export function Panel({ spec, onClose }: { spec: PanelSpec; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const timer = setTimeout(() => document.addEventListener('pointerdown', onPointerDown, true), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return (
    <div ref={ref} className="panel" role="dialog" aria-label={spec.title}>
      <div className="ph">
        <span>{spec.title}</span>
        <button type="button" className="btn icon sm" aria-label={he.app.close} onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <div className="pb">{spec.content}</div>
      {spec.footer ? <div className="pf">{spec.footer}</div> : null}
    </div>
  );
}

export function Sheet({ spec, onClose }: { spec: SheetSpec; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useFocusTrap(ref, onClose);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div ref={ref} className="sheet" role="dialog" aria-modal="true" aria-label={spec.title}>
        <div className="grip" aria-hidden="true" />
        <div className="spread">
          <h2>{spec.title}</h2>
          <button type="button" className="btn icon sm" aria-label={he.app.close} onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        {spec.content}
      </div>
    </>
  );
}

export function Drawer({ spec, onClose }: { spec: DrawerSpec; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useFocusTrap(ref, onClose);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div ref={ref} className="drawer" role="dialog" aria-modal="true" aria-label={spec.label}>
        <div className="dh">
          <h2>{spec.title}</h2>
          <button type="button" className="btn icon sm" aria-label={he.app.close} onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="db">{spec.content}</div>
        {spec.footer ? <div className="df">{spec.footer}</div> : null}
      </div>
    </>
  );
}

export function Dialog({ spec, onClose }: { spec: DialogSpec; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useFocusTrap(ref, onClose);
  return (
    <div className="dialog">
      <div className="scrim" onClick={onClose} />
      <div ref={ref} className={`box${spec.wide ? ' wide' : ''}`} role={spec.role ?? 'dialog'} aria-modal="true" aria-labelledby="dlg-title">
        <div className="dh">
          <h2 id="dlg-title">{spec.title}</h2>
          <button type="button" className="btn icon sm" aria-label={he.app.close} onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="db">
          {spec.text ? <p>{spec.text}</p> : null}
          {spec.content}
        </div>
        <div className="df">
          {spec.actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`btn${action.tone ? ` ${action.tone}` : ''}${action.start ? ' start' : ''}`}
              disabled={action.disabled}
              onClick={() => {
                const keepOpen = action.onClick?.();
                if (keepOpen !== false) onClose();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export interface ToastSpec {
  id: number;
  message: string;
  error?: boolean;
  action?: { label: string; onClick: () => void };
}

export function Toasts({ items, onDismiss }: { items: ToastSpec[]; onDismiss: (id: number) => void }) {
  return (
    <div className="toasts">
      {items.map((toast) => (
        <div key={toast.id} className={`toast${toast.error ? ' error' : ''}`} role={toast.error ? 'alert' : 'status'}>
          <Icon name={toast.error ? 'alertCircle' : 'checkCircle'} className="sm" />
          <span>{toast.message}</span>
          {toast.action ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                onDismiss(toast.id);
                toast.action?.onClick();
              }}
            >
              {toast.action.label}
            </button>
          ) : null}
          <button type="button" className="x" aria-label={he.app.close} onClick={() => onDismiss(toast.id)}>
            <Icon name="x" className="xs" />
          </button>
        </div>
      ))}
    </div>
  );
}
